import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import ERPLayout from "@/components/feature/ERPLayout";
import { deliverablesApi, jobCardsApi } from "@/services/api";

export interface DeliverableFile {
  id: number;
  jobNo: string;
  jobTitle: string;
  jobId: number;
  fileName: string;
  fileType: "PDF" | "DWG" | "KMZ" | "KML" | "XLSX" | "DXF" | "SHP" | "JPG" | "ZIP";
  category: "Survey Report" | "CAD Drawing" | "GIS Data" | "Photos" | "Excel Data" | "Final Package";
  version: string;
  description: string;
  status: "Draft" | "Under Review" | "Approved" | "Delivered";
  size: string;
  uploadedBy: string;
  uploadedAt: string;
  downloadCount: number;
  isLatest: boolean;
}
import FileDetailPanel from "./components/FileDetailPanel";

type ViewMode = "all" | "by-job";
type StatusFilter = "all" | "Draft" | "Under Review" | "Approved" | "Delivered";

interface JobOption {
  id: number;
  jobNo: string;
  title: string;
}

const fileTypeConfig: Record<string, { color: string; bg: string; icon: string }> = {
  PDF: { color: "#ef4444", bg: "#fef2f2", icon: "ri-file-pdf-line" },
  DWG: { color: "#f59e0b", bg: "#fffbeb", icon: "ri-file-code-line" },
  KMZ: { color: "#10b981", bg: "#ecfdf5", icon: "ri-map-2-line" },
  KML: { color: "#10b981", bg: "#ecfdf5", icon: "ri-map-2-line" },
  XLSX: { color: "#059669", bg: "#d1fae5", icon: "ri-file-excel-line" },
  DXF: { color: "#f59e0b", bg: "#fffbeb", icon: "ri-file-code-line" },
  SHP: { color: "#8b5cf6", bg: "#f5f3ff", icon: "ri-map-pin-line" },
  JPG: { color: "#64748b", bg: "#f8fafc", icon: "ri-image-line" },
  ZIP: { color: "#64748b", bg: "#f8fafc", icon: "ri-folder-zip-line" },
};

const statusConfig: Record<string, { color: string; bg: string }> = {
  Draft: { color: "#64748b", bg: "#f8fafc" },
  "Under Review": { color: "#f59e0b", bg: "#fffbeb" },
  Approved: { color: "#3b82f6", bg: "#eff6ff" },
  Delivered: { color: "#10b981", bg: "#ecfdf5" },
};

interface UploadModalProps {
  onClose: () => void;
  onUpload: (file: Partial<DeliverableFile>) => void;
  jobOptions: JobOption[];
}

function UploadModal({ onClose, onUpload, jobOptions }: UploadModalProps) {
  const [form, setForm] = useState({
    jobId: "",
    fileName: "",
    fileType: "PDF",
    category: "Survey Report",
    version: "v1.0",
    description: "",
    status: "Draft",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const job = jobOptions.find((j) => j.id === Number(form.jobId));
    onUpload({
      ...form,
      jobId: Number(form.jobId),
      jobNo: job?.jobNo || "",
      jobTitle: job?.title || "",
      fileType: form.fileType as DeliverableFile["fileType"],
      category: form.category as DeliverableFile["category"],
      status: form.status as DeliverableFile["status"],
      size: "—",
      uploadedBy: "Priya Sharma",
      uploadedAt: new Date().toISOString(),
      downloadCount: 0,
      isLatest: true,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">Upload Deliverable</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Job Card</label>
              <select
                required
                value={form.jobId}
                onChange={(e) => setForm({ ...form, jobId: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
              >
                <option value="">Select job card...</option>
                {jobOptions.map((j) => (
                  <option key={j.id} value={j.id}>{j.jobNo} — {j.title.slice(0, 40)}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">File Name</label>
              <input
                required
                type="text"
                value={form.fileName}
                onChange={(e) => setForm({ ...form, fileName: e.target.value })}
                placeholder="e.g. Survey_Report_v1.pdf"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">File Type</label>
              <select
                value={form.fileType}
                onChange={(e) => setForm({ ...form, fileType: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
              >
                {["PDF", "DWG", "KMZ", "KML", "XLSX", "DXF", "SHP", "JPG", "ZIP"].map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Version</label>
              <input
                type="text"
                value={form.version}
                onChange={(e) => setForm({ ...form, version: e.target.value })}
                placeholder="v1.0"
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
              >
                {["Survey Report", "CAD Drawing", "GIS Data", "Photos", "Excel Data", "Final Package"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
              >
                {["Draft", "Under Review", "Approved", "Delivered"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-gray-600 mb-1 block">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 500) })}
                placeholder="Brief description of this file..."
                rows={2}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 resize-none"
              />
              <p className="text-xs text-gray-400 text-right">{form.description.length}/500</p>
            </div>
          </div>

          <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-amber-300 hover:bg-amber-50/30 transition-all">
            <i className="ri-upload-cloud-2-line text-3xl text-gray-300 block mb-2" />
            <p className="text-sm text-gray-500">Click to select file or drag &amp; drop</p>
            <p className="text-xs text-gray-400 mt-1">PDF, DWG, KMZ, XLSX, ZIP supported</p>
          </div>

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-200 text-gray-600 rounded-xl py-2.5 text-sm font-semibold cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap">
              Cancel
            </button>
            <button type="submit" className="flex-1 bg-amber-600 text-white rounded-xl py-2.5 text-sm font-bold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap">
              <i className="ri-upload-2-line mr-1" />Upload File
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface VersionHistoryModalProps {
  jobNo: string;
  fileName: string;
  files: DeliverableFile[];
  onClose: () => void;
}

function VersionHistoryModal({ jobNo, fileName, files, onClose }: VersionHistoryModalProps) {
  const baseName = fileName.replace(/_v\d+\.\d+/, "").replace(/\.[^.]+$/, "");
  const versions = files.filter(
    (f) => f.jobNo === jobNo && f.fileName.replace(/_v\d+\.\d+/, "").replace(/\.[^.]+$/, "") === baseName
  ).sort((a, b) => b.version.localeCompare(a.version));

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">Version History</h3>
            <p className="text-xs text-gray-500 mt-0.5">{baseName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
            <i className="ri-close-line text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-3 max-h-96 overflow-y-auto">
          {versions.map((v) => {
            const ftCfg = fileTypeConfig[v.fileType] || fileTypeConfig.PDF;
            const stCfg = statusConfig[v.status] || statusConfig.Draft;
            return (
              <div key={v.id} className={`p-4 rounded-xl border ${v.isLatest ? "border-amber-200 bg-amber-50/30" : "border-gray-100"}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: ftCfg.bg }}>
                      <i className={`${ftCfg.icon} text-sm`} style={{ color: ftCfg.color }} />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-800">{v.version}</span>
                        {v.isLatest && <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-medium">Latest</span>}
                      </div>
                      <p className="text-xs text-gray-500">{v.uploadedBy} · {new Date(v.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</p>
                    </div>
                  </div>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap" style={{ color: stCfg.color, background: stCfg.bg }}>
                    {v.status}
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-2">{v.description}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-400">{v.size} · {v.downloadCount} downloads</span>
                  <button className="text-xs text-amber-600 font-medium hover:underline cursor-pointer whitespace-nowrap">
                    <i className="ri-download-line mr-1" />Download
                  </button>
                </div>
              </div>
            );
          })}
          {versions.length === 0 && (
            <p className="text-center text-gray-400 text-sm py-4">No version history found</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function DeliverablesPage() {
  const [viewMode, setViewMode] = useState<ViewMode>("all");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [jobFilter, setJobFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [versionModal, setVersionModal] = useState<{ jobNo: string; fileName: string } | null>(null);
  const [selectedFile, setSelectedFile] = useState<DeliverableFile | null>(null);
  const [files, setFiles] = useState<DeliverableFile[]>([]);
  const [jobOptions, setJobOptions] = useState<JobOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [successMsg, setSuccessMsg] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [notifToast, setNotifToast] = useState<{ fileName: string; jobNo: string } | null>(null);
  const navigate = useNavigate();

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(""), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [delivResult, jobResult] = await Promise.all([
        deliverablesApi.getAll(),
        jobCardsApi.getAll({ limit: 100 }),
      ]);
      const delivList = delivResult?.data || delivResult || [];
      if (Array.isArray(delivList)) setFiles(delivList);
      const jobList = jobResult?.data || jobResult || [];
      if (Array.isArray(jobList)) {
        setJobOptions(jobList.map((j: JobOption) => ({ id: j.id, jobNo: j.jobNo, title: j.title })));
      }
    } catch {
      setFiles([]);
      setJobOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpload = async (file: Partial<DeliverableFile>) => {
    try {
      const created = await deliverablesApi.create(file as Record<string, unknown>);
      const newFile: DeliverableFile = created?.data || created || { id: Date.now(), ...file } as DeliverableFile;
      setFiles((prev) => [newFile, ...prev]);
    } catch {
      const newFile: DeliverableFile = { id: Date.now(), ...file } as DeliverableFile;
      setFiles((prev) => [newFile, ...prev]);
    }
    showSuccess("File uploaded successfully");
  };

  const handleStatusChange = async (id: number, status: DeliverableFile["status"]) => {
    try {
      await deliverablesApi.updateStatus(id, status);
    } catch {
      // Optimistic update
    }
    const file = files.find((f) => f.id === id);
    setFiles((prev) => prev.map((f) => f.id === id ? { ...f, status } : f));
    if (selectedFile?.id === id) setSelectedFile((prev) => prev ? { ...prev, status } : null);
    if (status === "Delivered" && file) {
      setNotifToast({ fileName: file.fileName, jobNo: file.jobNo });
      setTimeout(() => setNotifToast(null), 5000);
    }
    showSuccess(`Status updated to ${status}`);
  };

  const handleDelete = async (id: number) => {
    try {
      await deliverablesApi.delete(id);
    } catch {
      // Optimistic delete
    }
    setFiles((prev) => prev.filter((f) => f.id !== id));
    if (selectedFile?.id === id) setSelectedFile(null);
    showSuccess("File removed");
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkStatusChange = async (status: DeliverableFile["status"]) => {
    const ids = Array.from(selectedIds);
    await Promise.allSettled(ids.map((id) => deliverablesApi.updateStatus(id, status)));
    setFiles((prev) => prev.map((f) => selectedIds.has(f.id) ? { ...f, status } : f));
    if (status === "Delivered") {
      const first = files.find((f) => selectedIds.has(f.id));
      if (first) {
        setNotifToast({ fileName: `${selectedIds.size} file(s)`, jobNo: first.jobNo });
        setTimeout(() => setNotifToast(null), 5000);
      }
    }
    showSuccess(`${selectedIds.size} file(s) updated to ${status}`);
    setSelectedIds(new Set());
  };

  const filtered = files.filter((f) => {
    if (statusFilter !== "all" && f.status !== statusFilter) return false;
    if (categoryFilter !== "all" && f.category !== categoryFilter) return false;
    if (jobFilter !== "all" && f.jobNo !== jobFilter) return false;
    if (search && !f.fileName.toLowerCase().includes(search.toLowerCase()) && !f.jobNo.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const uniqueJobs = Array.from(new Set(files.map((f) => f.jobNo)));
  const categories = Array.from(new Set(files.map((f) => f.category)));

  const byJob = uniqueJobs.map((jobNo) => ({
    jobNo,
    jobTitle: files.find((f) => f.jobNo === jobNo)?.jobTitle || "",
    files: filtered.filter((f) => f.jobNo === jobNo),
  })).filter((g) => g.files.length > 0);

  const totalFiles = files.length;
  const latestFiles = files.filter((f) => f.isLatest).length;
  const deliveredFiles = files.filter((f) => f.status === "Delivered").length;
  const reviewFiles = files.filter((f) => f.status === "Under Review").length;

  const FileRow = ({ f }: { f: DeliverableFile }) => {
    const ftCfg = fileTypeConfig[f.fileType] || fileTypeConfig.PDF;
    const stCfg = statusConfig[f.status] || statusConfig.Draft;
    const isSelected = selectedIds.has(f.id);
    const linkedJob = jobOptions.find((j) => j.jobNo === f.jobNo);
    return (
      <div
        className={`flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-all group cursor-pointer ${isSelected ? "bg-amber-50/40" : ""} ${selectedFile?.id === f.id ? "bg-amber-50/60 border-l-2 border-amber-500" : ""}`}
        onClick={() => setSelectedFile(f)}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={(e) => { e.stopPropagation(); toggleSelect(f.id); }}
          onClick={(e) => e.stopPropagation()}
          className="w-4 h-4 rounded accent-amber-600 cursor-pointer flex-shrink-0"
        />
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: ftCfg.bg }}>
          <i className={`${ftCfg.icon} text-lg`} style={{ color: ftCfg.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-gray-800 truncate max-w-xs">{f.fileName}</p>
            {f.isLatest && <span className="text-xs bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">Latest</span>}
            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full flex-shrink-0">{f.version}</span>
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            <span className="text-xs text-amber-700 font-medium">{f.jobNo}</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-500">{f.category}</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">{f.size}</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">{f.uploadedBy}</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-gray-400">{new Date(f.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>
          </div>
        </div>
        <div className="flex-shrink-0 hidden sm:block">
          <select
            value={f.status}
            onChange={(e) => { e.stopPropagation(); handleStatusChange(f.id, e.target.value as DeliverableFile["status"]); }}
            onClick={(e) => e.stopPropagation()}
            className="text-xs px-2 py-1 rounded-lg border-0 font-medium cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-500/30"
            style={{ color: stCfg.color, background: stCfg.bg }}
          >
            {["Draft", "Under Review", "Approved", "Delivered"].map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="flex-shrink-0 text-center hidden md:block">
          <p className="text-sm font-bold text-gray-700">{f.downloadCount}</p>
          <p className="text-xs text-gray-400">downloads</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          {linkedJob && (
            <button
              onClick={(e) => { e.stopPropagation(); navigate(`/job-cards/${linkedJob.id}`); }}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 cursor-pointer text-amber-600 transition-all"
              title={`Open Job Card: ${f.jobNo}`}
            >
              <i className="ri-file-list-3-line text-sm" />
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); setVersionModal({ jobNo: f.jobNo, fileName: f.fileName }); }}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500 hover:text-gray-700 transition-all"
            title="Version History"
          >
            <i className="ri-history-line text-sm" />
          </button>
          <button
            onClick={(e) => e.stopPropagation()}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-amber-50 cursor-pointer text-amber-600 transition-all"
            title="Download"
          >
            <i className="ri-download-line text-sm" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer text-red-400 hover:text-red-600 transition-all"
            title="Delete"
          >
            <i className="ri-delete-bin-line text-sm" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <ERPLayout title="Deliverables">
      {successMsg && (
        <div className="fixed top-4 right-4 z-50 bg-amber-600 text-white text-sm px-4 py-2 rounded-xl font-medium">
          <i className="ri-checkbox-circle-line mr-2" />{successMsg}
        </div>
      )}

      {notifToast && (
        <div className="fixed top-16 right-4 z-50 bg-white border border-amber-200 rounded-2xl p-4 w-80" style={{ boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-amber-50 rounded-xl flex-shrink-0">
              <i className="ri-notification-3-line text-amber-600 text-lg" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">Client Notified</p>
              <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                <strong className="text-gray-700">{notifToast.fileName}</strong> marked as Delivered for <strong className="text-amber-600">{notifToast.jobNo}</strong>. Client email notification sent.
              </p>
              <div className="flex items-center gap-1.5 mt-2">
                <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                <span className="text-xs text-amber-600 font-medium">Email dispatched successfully</span>
              </div>
            </div>
            <button onClick={() => setNotifToast(null)} className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0">
              <i className="ri-close-line text-sm" />
            </button>
          </div>
        </div>
      )}

      {showUpload && <UploadModal onClose={() => setShowUpload(false)} onUpload={handleUpload} jobOptions={jobOptions} />}

      {versionModal && (
        <VersionHistoryModal
          jobNo={versionModal.jobNo}
          fileName={versionModal.fileName}
          files={files}
          onClose={() => setVersionModal(null)}
        />
      )}

      {selectedFile && (
        <FileDetailPanel
          file={selectedFile}
          allFiles={files}
          onClose={() => setSelectedFile(null)}
          onStatusChange={handleStatusChange}
          onVersionHistory={(jobNo, fileName) => { setSelectedFile(null); setVersionModal({ jobNo, fileName }); }}
        />
      )}

      <div className="p-6 space-y-5">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Deliverables Management</h1>
            <p className="text-sm text-gray-500 mt-0.5">Upload, track, and manage CAD/PDF/KMZ files per job card</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className={`ri-refresh-line text-sm ${loading ? "animate-spin" : ""}`} /></span>
              Refresh
            </button>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 bg-amber-600 text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer hover:bg-amber-700 transition-all whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-upload-2-line text-sm" /></span>
              Upload File
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-gray-100 animate-pulse">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl bg-gray-100" />
                  <div className="space-y-1">
                    <div className="h-6 bg-gray-100 rounded w-8" />
                    <div className="h-3 bg-gray-100 rounded w-16" />
                  </div>
                </div>
              </div>
            ))
          ) : (
            [
              { label: "Total Files", value: totalFiles, icon: "ri-folder-3-line", color: "#10b981", bg: "#ecfdf5", sub: `${uniqueJobs.length} job cards` },
              { label: "Latest Versions", value: latestFiles, icon: "ri-file-check-line", color: "#3b82f6", bg: "#eff6ff", sub: "Active files" },
              { label: "Delivered", value: deliveredFiles, icon: "ri-truck-line", color: "#8b5cf6", bg: "#f5f3ff", sub: totalFiles > 0 ? `${Math.round((deliveredFiles / totalFiles) * 100)}% of total` : "0%" },
              { label: "Under Review", value: reviewFiles, icon: "ri-eye-line", color: "#f59e0b", bg: "#fffbeb", sub: "Awaiting approval" },
            ].map((s) => (
              <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: s.bg }}>
                    <i className={`${s.icon} text-lg`} style={{ color: s.color }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                    <p className="text-xs text-gray-500">{s.label}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-400">{s.sub}</p>
              </div>
            ))
          )}
        </div>

        {/* Bulk Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="bg-amber-600 text-white rounded-xl px-5 py-3 flex items-center justify-between">
            <span className="text-sm font-semibold">{selectedIds.size} file(s) selected</span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-amber-200">Change status to:</span>
              {["Under Review", "Approved", "Delivered"].map((s) => (
                <button
                  key={s}
                  onClick={() => handleBulkStatusChange(s as DeliverableFile["status"])}
                  className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold cursor-pointer transition-all whitespace-nowrap"
                >
                  {s}
                </button>
              ))}
              <button
                onClick={() => setSelectedIds(new Set())}
                className="ml-2 w-7 h-7 flex items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 cursor-pointer transition-all"
              >
                <i className="ri-close-line text-sm" />
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-48">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-gray-400 text-sm" />
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search files or job numbers..."
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
            >
              <option value="all">All Status</option>
              {["Draft", "Under Review", "Approved", "Delivered"].map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
            >
              <option value="all">All Categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-400 cursor-pointer"
            >
              <option value="all">All Jobs</option>
              {uniqueJobs.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
            <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
              <button
                onClick={() => setViewMode("all")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${viewMode === "all" ? "bg-white text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
              >
                <i className="ri-list-check mr-1" />All Files
              </button>
              <button
                onClick={() => setViewMode("by-job")}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${viewMode === "by-job" ? "bg-white text-gray-800" : "text-gray-500 hover:text-gray-700"}`}
              >
                <i className="ri-folder-3-line mr-1" />By Job
              </button>
            </div>
          </div>
        </div>

        {/* File List - All View */}
        {viewMode === "all" && (
          <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={selectedIds.size === filtered.length && filtered.length > 0}
                  onChange={(e) => setSelectedIds(e.target.checked ? new Set(filtered.map((f) => f.id)) : new Set())}
                  className="w-4 h-4 rounded accent-amber-600 cursor-pointer"
                />
                <span className="text-sm font-semibold text-gray-700">{filtered.length} file(s)</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span>Click a row to view details</span>
                <i className="ri-arrow-right-line" />
              </div>
            </div>
            {loading ? (
              <div className="divide-y divide-gray-50">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-4 px-5 py-4 animate-pulse">
                    <div className="w-4 h-4 bg-gray-100 rounded" />
                    <div className="w-10 h-10 rounded-xl bg-gray-100" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-100 rounded w-48" />
                      <div className="h-3 bg-gray-100 rounded w-64" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {filtered.map((f) => <FileRow key={f.id} f={f} />)}
                {filtered.length === 0 && (
                  <div className="text-center py-12 text-gray-400">
                    <i className="ri-folder-open-line text-4xl block mb-2" />
                    <p className="text-sm">No files match your filters</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* By Job View */}
        {viewMode === "by-job" && (
          <div className="space-y-4">
            {byJob.map((group) => (
              <div key={group.jobNo} className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                      <i className="ri-file-list-3-line text-amber-600 text-sm" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-800">{group.jobNo}</p>
                      <p className="text-xs text-gray-500">{group.jobTitle}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1">
                      {Array.from(new Set(group.files.map((f) => f.status))).map((s) => {
                        const sc = statusConfig[s] || statusConfig.Draft;
                        return (
                          <span key={s} className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ color: sc.color, background: sc.bg }}>
                            {group.files.filter((f) => f.status === s).length} {s}
                          </span>
                        );
                      })}
                    </div>
                    <span className="text-xs bg-white border border-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                      {group.files.length} file(s)
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {group.files.map((f) => {
                    const ftCfg = fileTypeConfig[f.fileType] || fileTypeConfig.PDF;
                    const stCfg = statusConfig[f.status] || statusConfig.Draft;
                    return (
                      <div
                        key={f.id}
                        className="flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-all group cursor-pointer"
                        onClick={() => setSelectedFile(f)}
                      >
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: ftCfg.bg }}>
                          <i className={`${ftCfg.icon} text-base`} style={{ color: ftCfg.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-gray-800 truncate">{f.fileName}</p>
                            {f.isLatest && <span className="text-xs bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded-full font-medium flex-shrink-0">Latest</span>}
                            <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full flex-shrink-0">{f.version}</span>
                          </div>
                          <p className="text-xs text-gray-400 mt-0.5">{f.category} · {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                        </div>
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap flex-shrink-0" style={{ color: stCfg.color, background: stCfg.bg }}>
                          {f.status}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={(e) => { e.stopPropagation(); setVersionModal({ jobNo: f.jobNo, fileName: f.fileName }); }}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-gray-500 transition-all"
                          >
                            <i className="ri-history-line text-sm" />
                          </button>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-amber-50 cursor-pointer text-amber-600 transition-all"
                          >
                            <i className="ri-download-line text-sm" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(f.id); }}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 cursor-pointer text-red-400 transition-all"
                          >
                            <i className="ri-delete-bin-line text-sm" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            {byJob.length === 0 && (
              <div className="bg-white rounded-xl border border-gray-100 text-center py-12 text-gray-400">
                <i className="ri-folder-open-line text-4xl block mb-2" />
                <p className="text-sm">No files match your filters</p>
              </div>
            )}
          </div>
        )}
      </div>
    </ERPLayout>
  );
}
